import React from 'react';
import { useFieldArray } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Dropzone from 'react-dropzone';

import { FormError, HelpText } from '@Forms/fields.jsx';
import { fileAsBase64 } from '@helpers';
import { imageMutation, imageQuery } from '@queries';
import {
  ButtonSpinnerDarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
} from '@svgs';

const ImageItem = ({
  field,
  fieldPrefix,
  index,
  itemsArray,
  register,
  setValue,
  watch,
}) => {
  const { data: imageData, isPending: imageIsPending } = useQuery(
    imageQuery(watch(`${fieldPrefix}.val`)),
  );

  return (
    <div className="sb-flex">
      {imageIsPending && <ButtonSpinnerDarkIcon />}
      {imageData && (
        <img src={`/${imageData.sizes.medium}`} style={{ maxWidth: '100px' }} />
      )}
      <input type="hidden" {...register(`${fieldPrefix}.label`)} />
      <input type="hidden" {...register(`${fieldPrefix}.val`)} />
      <button
        type="button"
        className="sb-btn"
        disabled={index === 0}
        onClick={() => itemsArray.move(index, index - 1)}
      >
        <ChevronUpIcon />
      </button>
      <button
        type="button"
        className="sb-btn"
        disabled={index === itemsArray.fields.length - 1}
        onClick={() => itemsArray.move(index, index + 1)}
      >
        <ChevronDownIcon />
      </button>
      <button
        type="button"
        className="sb-btn"
        onClick={() => {
          if (window.confirm('Are you sure to delete this record?')) {
            itemsArray.remove(index);
          }
        }}
      >
        <TrashIcon />
      </button>
    </div>
  );
};

const ImageItemsField = ({
  control,
  error,
  helpText,
  label,
  name,
  register,
  setValue,
  options = {},
  watch,
  ...props
}) => {
  // list of images that saves its label, val as image IDS

  const itemsArray = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: name, // unique name for your Field Array
    rules: options,
  });

  const mutation = useMutation(imageMutation());

  const itemsList = itemsArray.fields.map((field, index) => {
    const fieldPrefix = `${name}.${index}`;
    // nested datamodel fields
    return (
      <ImageItem
        key={field.id}
        field={field}
        fieldPrefix={fieldPrefix}
        index={index}
        itemsArray={itemsArray}
        register={register}
        setValue={setValue}
        watch={watch}
        mutation={mutation}
      />
    );
  });

  const onDrop = (file) => {
    // Adding a new image
    // get the image base64 data
    fileAsBase64(file, (data) => {
      // upload the image
      const payload = {
        type: 'image',
        upload: data,
      };
      mutation.mutate(payload, {
        onSuccess: (response) => {
          // add to list
          itemsArray.append({
            label: response.id,
            val: response.id,
          });
        },
      });
    });
  };

  return (
    <div
      className={
        `sb-form-item-formgroup ` + (error ? 'sb-form-item-error' : '')
      }
    >
      <label>{label}</label>
      <div className="sb-form-item-content">
        <div>
          {itemsList}
          <Dropzone
            accept={{
              'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.mpo', '.webp'],
            }}
            maxFiles={1}
            onDrop={onDrop}
          >
            {({ getRootProps, getInputProps }) => (
              <>
                {!mutation.isPending && (
                  <button
                    type="button"
                    className="sb-btn sb-btn-cancel"
                    {...getRootProps()}
                  >
                    Add <PlusIcon />
                  </button>
                )}
              </>
            )}
          </Dropzone>
          {mutation.isPending && (
            <button type="button" disabled className="sb-btn sb-btn-cancel">
              Uploading... <ButtonSpinnerDarkIcon />
            </button>
          )}
        </div>
        {helpText && <HelpText message={helpText} />}
        {error && error.root && <FormError message={error.root.message} />}
      </div>
    </div>
  );
};

export default ImageItemsField;
